import { useEffect, useState } from 'react';
import { AlertCircle, Rocket, ServerCog, XCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
    useCancelProvisioning,
    useProvisionDeployment,
    useProvisioningJobs,
    useProvisioningLog,
    useSshCredential,
} from '@/hooks/deployment';
import type { Deployment } from '@/services/deployment/deployment.service';
import { IN_FLIGHT_JOB_STATUSES, type ProvisioningErrorCode } from '@/services/provisioning';
import { ProvisionDialog } from './provision-dialog';
import { ProvisioningJobHistory } from './provisioning-job-history';
import { ProvisioningLogConsole } from './provisioning-log-console';
import { ProvisioningStatusHeader } from './provisioning-status-header';

/** What the operator should actually do about each failure kind. */
const REMEDY: Record<ProvisioningErrorCode, string> = {
    UNREACHABLE:
        "The host did not answer on SSH. Check the IP, that the server is up, and that port 22 is reachable from the provisioner.",
    AUTH_FAILED:
        "SSH refused the credentials. Either add the provisioner's fleet public key to the host, or set a per-host credential below.",
    TASK_FAILED: 'A provisioning step failed. The log below shows which one.',
    DNS_PREFLIGHT:
        'The domains do not resolve to this IP yet. Fix the DNS records and retry.',
    TIMEOUT: 'The run exceeded its time budget. Check the log for where it stalled.',
    INTERRUPTED:
        'The provisioner restarted mid-run, so the outcome is unknown and the host may be partly provisioned. Re-running is safe — the playbook converges.',
    INTERNAL: 'The provisioner failed before the playbook produced output.',
};

export function ProvisioningPanel({ deployment }: { deployment: Deployment }) {
    const { data: jobs, isLoading } = useProvisioningJobs(deployment.id);
    const { data: credential } = useSshCredential(deployment.id);
    const provisionMutation = useProvisionDeployment(deployment.id);
    const cancelMutation = useCancelProvisioning(deployment.id);

    // Set *only* by a click in the history table. Undefined means "follow the
    // newest attempt", which is what someone watching a run wants.
    //
    // This used to be a `selectedJobId` that an effect populated with the
    // latest id, guarded by `if (!selectedJobId)`. That guard could not tell
    // "the effect chose this a moment ago" from "the operator chose this", so
    // after the first attempt the panel was pinned forever: a retry created a
    // newer job, the old id still resolved through `jobs.find`, the `?? latest`
    // fallback never ran, and the console kept rendering the previous,
    // finished attempt. It looked correct on a first provision and broke on
    // every retry.
    const [pinnedJobId, setPinnedJobId] = useState<string | undefined>();
    const [dialogOpen, setDialogOpen] = useState(false);

    const latest = jobs?.[0];

    // A new attempt supersedes whatever was pinned — clicking Provision is a
    // statement about wanting to watch *this* run.
    useEffect(() => {
        setPinnedJobId(undefined);
    }, [latest?.id]);

    const selected = jobs?.find((j) => j.id === pinnedJobId) ?? latest;
    const log = useProvisioningLog(selected?.id, deployment.id, selected?.status);

    const liveStatus = log.status ?? selected?.status;
    const isRunning = !!liveStatus && IN_FLIGHT_JOB_STATUSES.includes(liveStatus);
    const canProvision =
        deployment.status === 'PENDING_DEPLOYMENT' ||
        deployment.status === 'PROVISION_FAILED';

    if (isLoading) {
        return <Skeleton className="h-64 w-full" />;
    }

    if (!deployment.filesDomain) {
        return (
            <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>No files domain set</AlertTitle>
                <AlertDescription>
                    Automated provisioning needs all three domains: the object store cannot serve
                    presigned URLs without one, so the tenant would come up silently broken. Add a
                    files domain on the Configuration tab first.
                </AlertDescription>
            </Alert>
        );
    }

    if (!jobs || jobs.length === 0) {
        return (
            <>
                <Card>
                    <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                        <ServerCog className="h-10 w-10 text-slate-400" />
                        <div className="space-y-1">
                            <p className="font-medium">This host has never been provisioned</p>
                            <p className="max-w-lg text-sm text-slate-500 dark:text-slate-400">
                                Provisioning connects over SSH, installs Docker, ships the tenant
                                stack and brings it up, then waits for the domains to answer before
                                marking the deployment live and emailing the customer.
                            </p>
                        </div>
                        <Button onClick={() => setDialogOpen(true)} disabled={!canProvision}>
                            <Rocket className="mr-2 h-4 w-4" />
                            Provision this host
                        </Button>
                    </CardContent>
                </Card>
                <ProvisionDialog
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                    defaultSshUser={deployment.sshUser ?? 'root'}
                    notifyEmail={deployment.subscription?.user?.email}
                    hasStoredCredential={!!credential}
                    isRetry={false}
                    isPending={provisionMutation.isPending}
                    onSubmit={(dto) =>
                        provisionMutation.mutate(dto, { onSuccess: () => setDialogOpen(false) })
                    }
                />
            </>
        );
    }

    return (
        <div className="space-y-6">
            {selected && (
                <Card>
                    <CardContent className="space-y-4 pt-6">
                        <ProvisioningStatusHeader
                            job={selected}
                            liveStatus={liveStatus}
                            verifyAttempt={log.verifyAttempt}
                        />

                        <div className="flex items-center gap-2">
                            {isRunning ? (
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button variant="destructive" size="sm">
                                            <XCircle className="mr-2 h-4 w-4" />
                                            Cancel run
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Cancel this run?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                Ansible stops on the controller side, but whatever the
                                                current task already did on the host stays done. The
                                                host may be left partly provisioned — re-running is the
                                                way to converge it.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Keep running</AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={() => cancelMutation.mutate(selected.id)}
                                            >
                                                Cancel the run
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            ) : (
                                canProvision && (
                                    <Button size="sm" onClick={() => setDialogOpen(true)}>
                                        <Rocket className="mr-2 h-4 w-4" />
                                        Retry provisioning
                                    </Button>
                                )
                            )}

                            {selected.stats && (
                                <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
                                    ok={selected.stats.ok} · changed={selected.stats.changed} ·
                                    failed={selected.stats.failures} · unreachable=
                                    {selected.stats.unreachable}
                                </span>
                            )}
                        </div>

                        {selected.errorMessage && (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertTitle>
                                    {selected.errorCode
                                        ? selected.errorCode.replace(/_/g, ' ').toLowerCase()
                                        : 'Provisioning failed'}
                                </AlertTitle>
                                <AlertDescription className="space-y-2">
                                    <p className="font-mono text-xs">{selected.errorMessage}</p>
                                    {selected.errorCode && <p>{REMEDY[selected.errorCode]}</p>}
                                </AlertDescription>
                            </Alert>
                        )}

                        {selected.healthReport && selected.healthReport.length > 0 && (
                            <div className="space-y-1 rounded-md border border-slate-200 p-3 text-sm dark:border-slate-800">
                                <p className="font-medium">Last domain check</p>
                                {selected.healthReport.map((r) => (
                                    <div key={r.domain} className="flex items-center gap-2">
                                        <span
                                            className={
                                                r.ok
                                                    ? 'h-1.5 w-1.5 rounded-full bg-emerald-500'
                                                    : 'h-1.5 w-1.5 rounded-full bg-red-500'
                                            }
                                        />
                                        <span className="font-mono">{r.domain}</span>
                                        <span className="text-slate-500 dark:text-slate-400">
                                            {r.ok ? `ok${r.status ? ` (${r.status})` : ''}` : r.error}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {log.error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{log.error}</AlertDescription>
                </Alert>
            )}

            <ProvisioningLogConsole
                events={log.events}
                isConnected={log.isConnected}
                isTerminal={log.isTerminal}
                truncated={log.truncated}
            />

            {jobs.length > 1 && (
                <div className="space-y-2">
                    <p className="text-sm font-medium">Attempts</p>
                    <ProvisioningJobHistory
                        jobs={jobs}
                        selectedJobId={selected?.id}
                        onSelect={setPinnedJobId}
                    />
                </div>
            )}

            <ProvisionDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                defaultSshUser={deployment.sshUser ?? 'root'}
                notifyEmail={deployment.subscription?.user?.email}
                hasStoredCredential={!!credential}
                isRetry
                isPending={provisionMutation.isPending}
                onSubmit={(dto) =>
                    provisionMutation.mutate(dto, { onSuccess: () => setDialogOpen(false) })
                }
            />
        </div>
    );
}
